import { useAuth } from '@/app/AuthContext.jsx';
import {
  useAllTasksList,
  useDocumentTitle,
  useExpFeatures,
  usePagosCola,
  usePagosHistorial,
  usePagosProceso,
  usePricing,
} from '@/hooks';
import { MS_PER_DAY, formatCLP, formatChileanDateShort } from '@/lib';
import { extractList } from '@/lib/apiList';
import { deadlineCalendarDays, isDeadlineExpired } from '@/lib/dateUtils';
import {
  downloadPayinReceipt,
  downloadTaxDocumentPdf,
  errorMessage,
  viewTaxDocumentPdf,
} from '@/lib/recepthor-api.js';
import { taxDocumentPdfFilename } from '@/lib/taxDocument';
import { CheckoutModal } from '@/shared';
import {
  Alert,
  FilterButton,
  FilterChips,
  PageHead,
  Pager,
  SearchField,
  SkeletonRows,
  SortHead,
  Th,
  useToast,
} from '@/ui';
import { DownloadIcon, SealCheckIcon, Wallet } from '@/ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import { TaxDocumentActions } from './TaxDocumentActions';
import { actuationLabel } from '@/lib/taskActuation.js';
import './Pagos.css';

/**
 * Pagos a receptores — three lifecycle tabs that follow the money:
 *   • Cola de pago  — quotes ACCEPTED (oferta recibida = "por pagar"), grouped
 *     by diligencia because payment is EXCLUSIVE (paying one closes its
 *     siblings). A checkbox table with a sticky paybar that sums the selection
 *     (honorarios + fee + IVA + total) and a "Pagar N" CTA.
 *   • En proceso    — PAYIN movements mid-flight (PENDING / PROCESSING /
 *     FAILED), read from GET /payments.
 *   • Pagadas       — PAYIN movements SUCCESS (the cobro confirmed; the payout
 *     to the receptor may still be pending), with a comprobante (boleta) link.
 *
 * The movement rows carry no case/tribunal fields by design; the task-side
 * columns (rol, tribunal, receptor, honorarios) correlate through GET /tasks
 * by taskId.
 *
 * Pay flow: a cola row NAVIGATES to the diligencia detail (/diligencias/:id) —
 * the single-request flow owns the checkout (CheckoutModal: pricing breakdown +
 * billing profile + POST /payments/payin → Toku redirect). The experimental
 * bulk «Pagar N» is the one in-place exception: it opens the shared
 * CheckoutModal here, one diligencia at a time (the API charges ONE quote per
 * payin — no batch endpoint), advancing a serial queue as each checkout closes.
 */

// Cola "elegida" label, driven by the diligencia's autopay preference. Falls
// back to "más barata" when no policy is set — the only case present in the
// current live data.
const AUTOPAY_LABEL = {
  barata: 'más barata',
  rapida: 'más rápida',
  valorada: 'mejor valorada',
};

function fmtCLP(value) {
  const n = Number(value);
  if (value == null || value === '' || Number.isNaN(n)) return '—';
  return formatCLP(n);
}

function daysUntil(iso, now = Date.now(), expiresAt) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (
    deadlineCalendarDays(iso, now, expiresAt) ??
    Math.ceil((t - now) / MS_PER_DAY)
  );
}

const CARATULA_MAX = 44;
function truncate(text, max) {
  if (!text || text.length <= max) return text || '';
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

const ts = (iso) => {
  const t = iso ? new Date(iso).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
};

// Cola secondary line: the causa/rol sits on tbl__secondary, the diligencia id
// leads in tbl__id. The rol is dropped when it already is the shown id (no
// displayId yet → API gap) so the line never reads "C-123 · C-123". Falls back
// to the rol when nothing else. Same shape as Diligencias.tableSecondaryOf.
function colaSecondary(g) {
  const rol = g.dilId === g.caseRol ? null : g.caseRol;
  const parts = [rol, g.actuacion, g.commune].filter(Boolean);
  return parts.join(' · ') || g.caseRol || '—';
}

// Comprobante (download) icon — the shared DownloadIcon; `.pg-dl` CSS keeps
// the 13px size / 1.7 stroke, and it inherits the accent color via
// currentColor inside the active .card__link.
const ICON_DL = <DownloadIcon className="pg-dl" />;

export default function Pagos() {
  useDocumentTitle('Pagos');
  // Bulk (multi-select + "Pagar N") is a Próx feature: gated behind the user's
  // experimental-features toggle. Off ⇒ single pay only (row click → detail).
  const [exp] = useExpFeatures();
  const { setBreadcrumb, setPills } = useOutletContext();
  const { organizationId } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  // Every row opens the diligencia detail — the single-request flow owns
  // payment / retry / boleta. (The exp bulk pay is the only in-place checkout.)
  const openDiligencia = useCallback(
    (taskId) => taskId && navigate(`/diligencias/${taskId}`),
    [navigate],
  );

  // Download of the own comprobante for a paid row: fetch + blob save against
  // the org-gated receipt endpoint, keyed by the movement's paymentId (d.id).
  const handleComprobante = useCallback(
    (d) =>
      downloadPayinReceipt(d.id, {
        organizationId,
        filename: d.rol !== '—' ? `comprobante-${d.rol}.pdf` : undefined,
      }).catch((err) =>
        toast({
          kind: 'err',
          message:
            err?.status === 404
              ? 'Comprobante no disponible.'
              : errorMessage(err, 'No se pudo descargar el comprobante.'),
        }),
      ),
    [organizationId, toast],
  );

  const handleViewFactura = useCallback(
    (doc, rol, prefix = 'factura') =>
      viewTaxDocumentPdf(doc.id, {
        organizationId,
        filename: taxDocumentPdfFilename(doc, { prefix, rol }),
      }).catch((err) =>
        toast({
          kind: 'err',
          message: errorMessage(err, 'No se pudo abrir la factura.'),
        }),
      ),
    [organizationId, toast],
  );

  const handleDownloadFactura = useCallback(
    (doc, rol, prefix = 'factura') =>
      downloadTaxDocumentPdf(doc.id, {
        organizationId,
        filename: taxDocumentPdfFilename(doc, { prefix, rol }),
      }).catch((err) =>
        toast({
          kind: 'err',
          message: errorMessage(err, 'No se pudo descargar la factura.'),
        }),
      ),
    [organizationId, toast],
  );

  const [tab, setTab] = useState('cola');
  const [search, setSearch] = useState('');
  // Receptor filter (multi) — options derived from the loaded rows.
  const [recSel, setRecSel] = useState([]);
  const [sortKey, setSortKey] = useState(null);
  const [sortBy, setSortBy] = useState('asc');
  const [pageSize, setPageSize] = useState(8);
  const [colaPage, setColaPage] = useState(1);
  const [procPage, setProcPage] = useState(1);
  const [histPage, setHistPage] = useState(1);

  // Checkbox selection in the cola, keyed by taskId. Seeded from the "Pagar N"
  // handoff (Diligencias passes its ready-to-pay selection via navigation
  // state) so the cola arrives pre-checked; ids not present in the loaded cola
  // are simply never matched by `chosen`.
  const [sel, setSel] = useState(() =>
    Object.fromEntries((location.state?.taskIds ?? []).map((id) => [id, true])),
  );

  // The exp bulk checkout's open diligencia (in-place CheckoutModal, «Pagar N»
  // only — single pay navigates to the detail instead).
  const [openTaskId, setOpenTaskId] = useState(null);
  // When "Pagar N" selects several, we pay them one-by-one: this is the queue of
  // remaining taskIds after the currently-open one.
  const [payQueue, setPayQueue] = useState([]);

  useEffect(() => {
    setBreadcrumb(['Pagos']);
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills]);

  // Reset paging + sort + filters + selection when switching tabs.
  const switchTab = useCallback((t) => {
    setTab(t);
    setSearch('');
    setRecSel([]);
    setSortKey(null);
    setSortBy('asc');
    setColaPage(1);
    setProcPage(1);
    setHistPage(1);
    setSel({});
  }, []);

  // Drop the (Próx) selection when experimental features are turned off, so a
  // hidden selection can't silently reappear if the user re-enables them.
  useEffect(() => {
    if (!exp) setSel({});
  }, [exp]);

  // Any search / filter / sort change resets the active tab to page 1.
  useEffect(() => {
    setColaPage(1);
    setProcPage(1);
    setHistPage(1);
  }, [search, recSel, sortKey, sortBy, pageSize]);

  // ── COLA — ACCEPTED quotes, org-wide, grouped by diligencia ──
  const {
    data: quotesData,
    isLoading: quotesLoading,
    isError: quotesError,
    refetch: refetchCola,
  } = usePagosCola(organizationId);

  // ── EN PROCESO — PAYIN movements mid-flight ──
  const {
    data: procData,
    isLoading: procLoading,
    isError: procError,
    refetch: refetchProceso,
  } = usePagosProceso(organizationId);

  // ── PAGADAS — PAYIN movements SUCCESS ──
  const {
    data: histData,
    isLoading: histLoading,
    isError: histError,
    refetch: refetchHistorial,
  } = usePagosHistorial(organizationId);

  // Task-side columns for the movement rows (rol, tribunal, receptor,
  // honorarios) plus the stage and the saved link the Acción cell acts on,
  // correlated by taskId. Auxiliary: a failed tasks read renders '—' cells,
  // never the tab's error state (that one belongs to the payments query).
  //
  // Only a DRAFT task can never hold a payin, so the correlation is spent on
  // tasks a movement can point at rather than on the most recently touched
  // task of any phase. QUOTING stays in: a charge that expires sends the task
  // back to PAYMENT_DUE, which the task list serves as QUOTING, and its
  // failed payin still needs the task's columns. Every page is read, because a
  // row whose task is missing falls back to the columns it can render. The
  // correlation goes away once the movement carries these fields itself
  // (REC-1521).
  const { data: tasksData } = useAllTasksList(
    { status: 'QUOTING,PROCESSING_PAYMENT,IN_PROGRESS,ENDED,CANCELLED' },
    { enabled: organizationId != null },
  );
  const taskById = useMemo(() => {
    const map = new Map();
    for (const t of extractList(tasksData)) map.set(t.id, t);
    return map;
  }, [tasksData]);

  // Tiered Recepthor fee for the org's next diligencia — GET /pricing/calculate,
  // the same source as the CheckoutModal, replacing the old flat-FEE mirror
  // (a flat client-side fee diverged from the backend's tramos). Display-only:
  // the backend recomputes at pay time. While unavailable (loading / error) the
  // fee-dependent cells render '—' — we never invent a total (CheckoutModal
  // precedent).
  const { data: pricingData } = usePricing({
    enabled: organizationId != null,
  });
  const feePrice = (pricingData?.data ?? pricingData)?.price ?? null;
  const feeNet = feePrice != null ? Number(feePrice.netAmount) || 0 : null;
  const feeIva = feePrice != null ? Number(feePrice.ivaAmount) || 0 : null;
  const feeTotal = feePrice != null ? Number(feePrice.totalAmount) || 0 : null;

  // Cost breakdown of one un-paid offer (cola rows / paybar): honorarios
  // pass-through + tiered fee + IVA (IVA applies to the service only). Null
  // fee ⇒ null total ⇒ '—' in the UI.
  const costOf = useCallback(
    (honorarios) => {
      const hon = Number(honorarios) || 0;
      return {
        honorarios: hon,
        fee: feeNet,
        iva: feeIva,
        total: feeTotal != null ? hon + feeTotal : null,
      };
    },
    [feeNet, feeIva, feeTotal],
  );

  // Group ACCEPTED quotes by diligencia; the cheapest offer leads the row. Rows
  // whose plazo already lapsed leave the cola (handled in the Expirada surface).
  const groups = useMemo(() => {
    const now = Date.now();
    const list = Array.isArray(quotesData?.results)
      ? quotesData.results
      : extractList(quotesData);
    const byTask = new Map();
    for (const q of list) {
      if (!q.taskId) continue;
      const g = byTask.get(q.taskId) ?? {
        taskId: q.taskId,
        // Diligencia ID for the tbl__id slot: prefer the human displayId / code,
        // falling back to the rol. Mirrors Diligencias.dilId.
        dilId: q.displayId ?? q.code ?? q.caseRol ?? '—',
        caseRol: q.caseRol ?? null,
        caseTitle: q.caseTitle ?? '',
        commune: q.taskCommune ?? q.commune ?? q.comuna ?? '',
        actuacion:
          actuationLabel(q) ?? q.notificationType ?? q.actuacion ?? null,
        deadline: q.taskDeadline ?? null,
        deadlineExpiresAt: q.taskDeadlineExpiresAt ?? null,
        autopay: q.autopay ?? null,
        offers: [],
      };
      g.offers.push({
        id: q.id,
        name: q.receptor?.name ?? q.recipientName ?? 'Receptor',
        price: Number(q.price) || 0,
        rawPrice: q.price,
        createdAt: q.createdAt ?? null,
        dateOffered: q.dateOffered ?? null,
        updatedAt: q.updatedAt ?? null,
      });
      byTask.set(q.taskId, g);
    }
    const out = [];
    for (const g of byTask.values()) {
      const left = daysUntil(g.deadline, now, g.deadlineExpiresAt);
      const expired =
        isDeadlineExpired(g.deadline, g.deadlineExpiresAt, now) ??
        (left != null && left < 0);
      if (expired) continue; // expired → out of the cola
      g.offers.sort((a, b) => a.price - b.price);
      g.best = g.offers[0];
      out.push(g);
    }
    return out;
  }, [quotesData]);

  // Map a /payments PAYIN row → its display fields:
  //   settledAmount → the real charged total (quote + tiered fee + IVA),
  //                   masked (null) by the server until status SUCCESS.
  //   task fields   → rol / tribunal / receptor (payeeName) / honorarios
  //                   (paidAmount), correlated by taskId.
  // Unknown amounts stay null and render '—'; the client never re-derives
  // money with its own fee math.
  const mapMovement = useCallback(
    (p) => {
      const t = taskById.get(p.taskId) ?? null;
      const settled = Number(p.settledAmount);
      return {
        id: p.paymentId,
        taskId: p.taskId ?? null,
        rol: t?.caseRol ?? '—',
        title: t?.tribunalName ?? t?.caseTitle ?? 'Diligencia',
        // The holder of the account the receptor committed on the PAID quote.
        receptor: t?.payeeName ?? '—',
        status: p.status ?? null,
        // The movement's status cannot tell a declined card apart from a
        // charge still confirming: one Toku invoice admits the retry inside the
        // same checkout, so a decline leaves the payin live and only the task's
        // stage records it. Correlated here like every other task-side column,
        // because the payments module serves no task fields by design.
        stage: t?.stage ?? null,
        honorarios: t?.paidAmount != null ? Number(t.paidAmount) || 0 : null,
        // Real charged total (settledAmount) or null → '—'.
        total:
          p.settledAmount != null && !Number.isNaN(settled) ? settled : null,
        paidAt: p.settledAt ?? p.updatedAt ?? p.createdAt ?? null,
        dateOffered: t?.dateOffered ?? null,
        // Checkout link the live payin froze, so an unconfirmed charge in En
        // proceso offers the way back to the Pasarela instead of a dash. Null
        // on a settled or failed movement.
        paymentLink: t?.paymentLink ?? null,
        // Own comprobante archived in S3 (the movement's voucher) — feeds
        // the Pagadas "Boleta" control; false while archiving is in flight.
        hasReceipt: p.hasReceipt === true,
        // Tax documents of the charge: the SII invoice and the credit notes
        // that correct it. They hang off the movement, which is where the
        // model keeps them; Ver/Descargar stay visible and disabled until the
        // document is emitted.
        taxDocument: p.taxDocument ?? null,
        creditNotes: Array.isArray(p.creditNotes) ? p.creditNotes : [],
      };
    },
    [taskById],
  );

  // En proceso — payins mid-flight (the payments query is the source of truth;
  // no optimistic session state, so a closed checkout simply refetches here).
  const procRows = useMemo(
    () =>
      extractList(procData)
        .filter((p) => ['PENDING', 'PROCESSING', 'FAILED'].includes(p.status))
        .map(mapMovement),
    [procData, mapMovement],
  );

  const histRows = useMemo(
    () => extractList(histData).map(mapMovement),
    [histData, mapMovement],
  );

  // Receptor filter options — from the active tab's rows.
  const recOptions = useMemo(() => {
    const names = new Set();
    if (tab === 'cola') {
      groups.forEach((g) => g.offers.forEach((o) => names.add(o.name)));
    } else if (tab === 'proceso') {
      procRows.forEach((d) => d.receptor && names.add(d.receptor));
    } else {
      histRows.forEach((d) => d.receptor && names.add(d.receptor));
    }
    return [...names]
      .filter((n) => n && n !== '—')
      .sort((a, b) => a.localeCompare(b))
      .map((n) => ({ value: n, label: n }));
  }, [tab, groups, procRows, histRows]);

  const filterGroups = useMemo(
    () => [
      {
        key: 'receptor',
        label: 'Receptor',
        value: recSel,
        options: recOptions,
        onChange: setRecSel,
      },
    ],
    [recSel, recOptions],
  );

  const ql = search.trim().toLowerCase();
  const matchQ = useCallback(
    (...fields) =>
      !ql || fields.some((f) => (f || '').toLowerCase().includes(ql)),
    [ql],
  );
  const matchRec = useCallback(
    (...names) => recSel.length === 0 || names.some((n) => recSel.includes(n)),
    [recSel],
  );

  // ── COLA: filter + sort + paginate ──
  const colaFiltered = useMemo(
    () =>
      groups.filter(
        (g) =>
          matchQ(
            g.dilId,
            g.caseRol,
            g.caseTitle,
            ...g.offers.map((o) => o.name),
          ) && matchRec(...g.offers.map((o) => o.name)),
      ),
    [groups, matchQ, matchRec],
  );
  const colaSorted = useMemo(() => {
    if (!sortKey) return colaFiltered;
    const dir = sortBy === 'asc' ? 1 : -1;
    const val = {
      dil: (g) => (g.caseTitle || g.dilId || '').toLowerCase(),
      receptor: (g) => (g.best?.name || '').toLowerCase(),
      comprometida: (g) => ts(g.best?.dateOffered),
      hon: (g) => g.best?.price || 0,
      // Null total (fee not loaded) sorts as honorarios-only: stable + cheap.
      total: (g) => costOf(g.best?.price).total ?? g.best?.price ?? 0,
    }[sortKey];
    if (!val) return colaFiltered;
    return [...colaFiltered].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
  }, [colaFiltered, sortKey, sortBy, costOf]);

  // Shared movement-row sorter for En proceso / Pagadas (all columns sortable).
  const sortMovements = useCallback(
    (rows) => {
      if (!sortKey) return rows;
      const dir = sortBy === 'asc' ? 1 : -1;
      const val = {
        dil: (d) => (d.title || d.rol || '').toLowerCase(),
        receptor: (d) => (d.receptor || '').toLowerCase(),
        // One rank per label the Estado cell can draw, in the order the work
        // reads: the attempt that closed, the card the provider declined, and
        // the charge still confirming. Ranking by `status` alone put a declined
        // row, which asks the firm to pay again, among the ones that ask for
        // nothing.
        estado: (d) =>
          d.status === 'FAILED' ? 0 : d.stage === 'PAYMENT_REJECTED' ? 1 : 2,
        hon: (d) => d.honorarios || 0,
        // Unknown totals (payin not confirmed / legacy row) sort first on asc.
        total: (d) => (d.total != null ? d.total : -1),
        pagada: (d) => ts(d.paidAt),
      }[sortKey];
      if (!val) return rows;
      return [...rows].sort((a, b) => {
        const x = val(a);
        const y = val(b);
        return (x < y ? -1 : x > y ? 1 : 0) * dir;
      });
    },
    [sortKey, sortBy],
  );

  const procFiltered = useMemo(
    () =>
      sortMovements(
        procRows.filter(
          (d) => matchQ(d.rol, d.title, d.receptor) && matchRec(d.receptor),
        ),
      ),
    [procRows, matchQ, matchRec, sortMovements],
  );
  const histFiltered = useMemo(
    () =>
      sortMovements(
        histRows.filter(
          (d) => matchQ(d.rol, d.title, d.receptor) && matchRec(d.receptor),
        ),
      ),
    [histRows, matchQ, matchRec, sortMovements],
  );

  // Pagers (client-side; the cola is aggregated in memory).
  const page = (rows, p) => rows.slice((p - 1) * pageSize, p * pageSize);
  const colaSlice = page(colaSorted, colaPage);
  const procSlice = page(procFiltered, procPage);
  const histSlice = page(histFiltered, histPage);

  // ── selection + paybar totals ──
  // Fee/IVA/total go null (→ '—') until the tiered fee loads. N × fee is an
  // estimate: a batch can cross a tramo boundary mid-way; the checkout / the
  // backend recompute the authoritative number per payin.
  const chosen = colaFiltered.filter((g) => sel[g.taskId]);
  const chosenHon = chosen.reduce(
    (n, g) => n + (Number(g.best?.price) || 0),
    0,
  );
  const totals = {
    hon: chosenHon,
    fee: feeNet != null ? feeNet * chosen.length : null,
    iva: feeIva != null ? feeIva * chosen.length : null,
    total: feeTotal != null ? chosenHon + feeTotal * chosen.length : null,
  };

  // ── exp bulk checkout (real pay flow, CheckoutModal) ──
  const openGroup = useMemo(
    () => groups.find((g) => g.taskId === openTaskId) ?? null,
    [groups, openTaskId],
  );
  const [pickedOfferId, setPickedOfferId] = useState(null);
  const pickedOffer = useMemo(() => {
    if (!openGroup) return null;
    return (
      openGroup.offers.find((o) => o.id === pickedOfferId) ??
      openGroup.best ??
      null
    );
  }, [openGroup, pickedOfferId]);

  const openCheckout = useCallback(
    (taskId, queue = []) => {
      const g = groups.find((x) => x.taskId === taskId);
      setOpenTaskId(taskId);
      setPickedOfferId(g?.best?.id ?? null);
      setPayQueue(queue);
    },
    [groups, setOpenTaskId],
  );
  const closeCheckout = useCallback(() => {
    setOpenTaskId(null);
    setPickedOfferId(null);
    setPayQueue([]);
  }, [setOpenTaskId]);

  // "Pagar N": pay the selected diligencias one by one. Opens the first; the
  // rest queue up. (The API charges one quote per payin — no batch endpoint.)
  const paySelected = useCallback(() => {
    const ids = chosen.map((g) => g.taskId);
    if (!ids.length) return;
    openCheckout(ids[0], ids.slice(1));
  }, [chosen, openCheckout]);

  const isLoading =
    organizationId == null ||
    (tab === 'cola'
      ? quotesLoading
      : tab === 'proceso'
        ? procLoading
        : histLoading);

  // A failed fetch on the active tab surfaces an error state (not a silent
  // empty table) with a retry — the payment hooks do not swallow errors.
  const isError =
    tab === 'cola' ? quotesError : tab === 'proceso' ? procError : histError;
  const refetchActive =
    tab === 'cola'
      ? refetchCola
      : tab === 'proceso'
        ? refetchProceso
        : refetchHistorial;

  const onSortChange = (key, dir) => {
    setSortKey(key);
    setSortBy(dir);
  };
  const sortProps = { sortKey, sortBy, onSort: onSortChange };

  const colaCount = groups.length;
  const procCount = procRows.length;
  const histCount = histRows.length;

  // ── Content gate ─────────────────────────────────────────────────────────
  // The whole content region is gated, NOT the table interior:
  //   loading      → headerless table skeleton (no column heads while loading)
  //   empty source → a single centered .empty card (per-tab title; cola adds a
  //                  hint) — the table + paybar are omitted entirely
  //   no results   → a centered .empty card with the "Sin resultados" copy
  //   otherwise    → the active tab's table (+ cola paybar)
  const sourceCount =
    tab === 'cola' ? colaCount : tab === 'proceso' ? procCount : histCount;
  const filteredCount =
    tab === 'cola'
      ? colaSorted.length
      : tab === 'proceso'
        ? procFiltered.length
        : histFiltered.length;
  const isEmptySource = !isLoading && !isError && sourceCount === 0;
  const isNoResults =
    !isLoading && !isError && !isEmptySource && filteredCount === 0;
  // The table shows whenever the active tab has rows to render (past loading).
  // Deliberately NOT gated on !isError: a background-refetch failure keeps the
  // already-loaded rows on screen (the error surfaces as a banner above) rather
  // than blanking a money view — mirrors ComprobantesPagoTabla.
  const showTable = !isLoading && filteredCount > 0;
  const emptyTitle =
    tab === 'cola'
      ? 'No hay diligencias por pagar'
      : tab === 'proceso'
        ? 'Sin pagos en proceso'
        : 'Aún no hay pagos';

  const rowNow = Date.now();

  return (
    <div className="page reveal">
      <PageHead
        kicker="Finanzas"
        title="Pagos a receptores"
        sub={
          <>
            Diligencias con oferta elegida, listas para pagar. Recepthor cobra{' '}
            <span
              className="tip"
              data-tip={`Fee Recepthor ${fmtCLP(feeNet)} + IVA por diligencia (Tramo vigente)`}
            >
              honorarios + fee + IVA
            </span>{' '}
            y le transfiere los honorarios al receptor.
          </>
        }
      />

      <div className="tabs">
        <button
          type="button"
          className={`tab${tab === 'cola' ? ' tab--on' : ''}`}
          onClick={() => switchTab('cola')}
        >
          Cola de pago
          <span className="tab__n">{colaCount}</span>
        </button>
        <button
          type="button"
          className={`tab${tab === 'proceso' ? ' tab--on' : ''}`}
          onClick={() => switchTab('proceso')}
        >
          En proceso
          <span className="tab__n">{procCount}</span>
        </button>
        <button
          type="button"
          className={`tab${tab === 'hist' ? ' tab--on' : ''}`}
          onClick={() => switchTab('hist')}
        >
          Pagadas
          <span className="tab__n">{histCount}</span>
        </button>
      </div>

      <div className="toolbar">
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder="Buscar por ID, carátula o receptor…"
        />
        <div className="toolbar__spacer" />
        <FilterButton groups={filterGroups} />
      </div>

      <FilterChips groups={filterGroups} />

      {/* Loading → headerless full-bleed skeleton: no column heads appear
          while the list loads. */}
      {isLoading && (
        <div className="tbl-wrap" style={{ marginTop: 14 }}>
          <table className="tbl">
            <tbody>
              <SkeletonRows rows={6} cols={6} />
            </tbody>
          </table>
        </div>
      )}

      {/* Error → a non-destructive banner (role=alert) above the content, with
          a retry. On initial-load failure it stands alone; on a background
          refetch failure the table below stays put. Mirrors the
          sibling ComprobantesPagoTabla error idiom. */}
      {!isLoading && isError && (
        <div style={{ marginTop: 14, marginBottom: 14 }}>
          <Alert
            tone="error"
            role="alert"
            title="No se pudieron cargar los pagos"
          >
            <div>
              Ocurrió un error al cargar la información. Por favor, intenta
              nuevamente.
            </div>
            <button
              type="button"
              className="t-btn t-btn--secondary t-btn--sm"
              style={{ marginTop: 8 }}
              onClick={() => refetchActive()}
            >
              Reintentar
            </button>
          </Alert>
        </div>
      )}

      {/* Empty source → a single centered .empty card; the table + paybar are
          omitted entirely (the whole region is gated, not just the table). */}
      {isEmptySource && (
        <div className="empty">
          <div className="empty__t">{emptyTitle}</div>
          {tab === 'cola' && (
            <div className="note" style={{ marginTop: 6 }}>
              Cuando elijas una oferta en una diligencia, aparecerá aquí.
            </div>
          )}
        </div>
      )}

      {/* No results → centered .empty card with the formal copy. */}
      {isNoResults && (
        <div className="empty">
          <div className="empty__t">Sin resultados para “{search}”</div>
          <div className="note" style={{ marginTop: 6 }}>
            Revise el ID, la carátula o el receptor.
          </div>
        </div>
      )}

      {showTable && tab === 'cola' && (
        <>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {/* Próx: columna de selección múltiple (pago en lote). */}
                  {exp && <th style={{ width: 32 }} aria-label="Seleccionar" />}
                  <SortHead label="Diligencia" k="dil" {...sortProps} />
                  <SortHead
                    label="Oferta · receptor"
                    k="receptor"
                    w="22%"
                    {...sortProps}
                  />
                  <SortHead
                    label="Comprometida"
                    k="comprometida"
                    w="13%"
                    num
                    tip="Fecha que el receptor comprometió al ofertar"
                    {...sortProps}
                  />
                  <SortHead
                    label="Honorarios"
                    k="hon"
                    w="12%"
                    num
                    tip="Honorarios del receptor (pass-through)"
                    {...sortProps}
                  />
                  <Th
                    w="13%"
                    num
                    tip={`Fee Recepthor ${fmtCLP(feeNet)} + IVA ${fmtCLP(feeIva)}`}
                  >
                    Fee + IVA
                  </Th>
                  <SortHead
                    label="Total"
                    k="total"
                    w="12%"
                    num
                    {...sortProps}
                  />
                </tr>
              </thead>
              <tbody>
                {colaSlice.map((g) => {
                  const c = costOf(g.best?.price);
                  const left = daysUntil(
                    g.deadline,
                    rowNow,
                    g.deadlineExpiresAt,
                  );
                  const urgent = left != null && left <= 1;
                  const n = g.offers.length;
                  return (
                    <tr
                      key={g.taskId}
                      data-urgent={urgent ? 'true' : undefined}
                      onClick={() => openDiligencia(g.taskId)}
                    >
                      {exp && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <label className="t-check">
                            <input
                              type="checkbox"
                              checked={!!sel[g.taskId]}
                              onChange={() =>
                                setSel((s) => ({
                                  ...s,
                                  [g.taskId]: !s[g.taskId],
                                }))
                              }
                            />
                          </label>
                        </td>
                      )}
                      <td>
                        <div className="tbl__id">{g.dilId}</div>
                        <div
                          className="tbl__primary"
                          style={{ fontWeight: 400, marginTop: 3 }}
                        >
                          {truncate(g.caseTitle, CARATULA_MAX) || '—'}
                        </div>
                        <div className="tbl__secondary">{colaSecondary(g)}</div>
                      </td>
                      <td>
                        <div
                          className="tbl__primary"
                          style={{ fontWeight: 400 }}
                        >
                          {g.best?.name}
                        </div>
                        <div className="tbl__secondary">
                          {n} {n === 1 ? 'oferta' : 'ofertas'} · elegida:{' '}
                          {AUTOPAY_LABEL[g.autopay] || 'más barata'}
                        </div>
                      </td>
                      <td className="num">
                        {formatChileanDateShort(g.best?.dateOffered) ?? '—'}
                      </td>
                      <td className="num">{fmtCLP(c.honorarios)}</td>
                      <td className="num note">
                        {fmtCLP(c.fee != null ? c.fee + c.iva : null)}
                      </td>
                      <td className="num tbl__primary">{fmtCLP(c.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pager
              page={colaPage}
              pageCount={Math.max(1, Math.ceil(colaSorted.length / pageSize))}
              total={colaSorted.length}
              pageSize={pageSize}
              onPage={setColaPage}
              onPageSize={(n) => {
                setPageSize(n);
                setColaPage(1);
              }}
              sizes={[8, 16, 24, 48]}
              unit="diligencias"
            />
          </div>

          {/* Próx: bulk pay (running-total bar + "Pagar N"). Enabled journey:
              single pay — clicking a row navigates to that diligencia's
              detail, where the checkout lives. */}
          {exp && (
            <div className="paybar">
              <div className="paybar__sel">
                <span className="paybar__count">
                  <b>{chosen.length}</b> de {colaFiltered.length}{' '}
                  {colaFiltered.length === 1 ? 'diligencia' : 'diligencias'}{' '}
                  seleccionada{chosen.length === 1 ? '' : 's'}
                </span>
                <span className="paybar__break">
                  honorarios {fmtCLP(totals.hon)} · fee {fmtCLP(totals.fee)} ·
                  IVA {fmtCLP(totals.iva)}
                </span>
              </div>
              <div className="paybar__total">
                <span className="paybar__total-lbl">Total a pagar</span>
                <span className="paybar__total-val">
                  {fmtCLP(totals.total)}
                </span>
              </div>
              <button
                type="button"
                className="t-btn t-btn--primary t-btn--lg paybar__cta"
                disabled={chosen.length === 0}
                onClick={paySelected}
              >
                <Wallet size={16} />
                Pagar {chosen.length}{' '}
                {chosen.length === 1 ? 'diligencia' : 'diligencias'}
              </button>
            </div>
          )}
        </>
      )}

      {showTable && tab === 'proceso' && (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <SortHead label="Diligencia" k="dil" {...sortProps} />
                <SortHead label="Estado" k="estado" w="26%" {...sortProps} />
                <SortHead label="Total" k="total" w="16%" num {...sortProps} />
                <Th w="18%" num>
                  Acción
                </Th>
              </tr>
            </thead>
            <tbody>
              {procSlice.map((d) => {
                // The charged total (settledAmount) unmasks with the payin
                // confirmation, so a mid-flight movement legitimately shows
                // '—' (never a client-side guess) next to its "Confirmando el
                // cobro" state.
                // Two facts share the corrective treatment and only one closed
                // the charge. `closed` is an attempt a sweep or a cancellation
                // ended. `declined` is a card the provider rejected with the
                // movement still live, which the stage is the only place to
                // read: the row used to say "Confirmando el cobro" over it,
                // the opposite of what the firm has to do about it.
                const closed = d.status === 'FAILED';
                const declined = !closed && d.stage === 'PAYMENT_REJECTED';
                const needsAction = closed || declined;
                return (
                  <tr
                    key={d.id}
                    onClick={() => d.taskId && openDiligencia(d.taskId)}
                  >
                    <td>
                      <div className="tbl__id">{d.rol}</div>
                      <div
                        className="tbl__primary"
                        style={{ fontWeight: 400, marginTop: 3 }}
                      >
                        {truncate(d.title, CARATULA_MAX)}
                      </div>
                    </td>
                    <td>
                      {needsAction ? (
                        <span className="st st--err">
                          <span className="st__dot" />
                          {/* The declined wording is the one the diligencia
                              view already uses for this stage, so the two
                              screens name the same fact the same way. */}
                          {closed ? 'El cobro falló' : 'Pago no completado'}
                        </span>
                      ) : (
                        <span className="st st--warn">
                          <span className="st__dot" />
                          Confirmando el cobro
                        </span>
                      )}
                    </td>
                    <td className="num tbl__primary">{fmtCLP(d.total)}</td>
                    <td className="num" onClick={(e) => e.stopPropagation()}>
                      {closed && d.taskId ? (
                        <button
                          type="button"
                          className="t-btn t-btn--primary t-btn--sm"
                          onClick={() => openDiligencia(d.taskId)}
                        >
                          Reintentar
                        </button>
                      ) : d.paymentLink ? (
                        // An unconfirmed charge is often an abandoned checkout,
                        // and the row had no action for it. The link is the one
                        // the attempt froze: Toku owns the invoice and takes a
                        // single payment on it. Not a button — it leaves the
                        // app. A declined card reaches the same link, and that
                        // is the retry: the invoice is still open.
                        <a
                          className="t-btn t-btn--primary t-btn--sm"
                          href={d.paymentLink}
                        >
                          Retomar
                        </a>
                      ) : declined && d.taskId ? (
                        // Declined with no saved link: the detail is where the
                        // same payment is retried, so the row leads there
                        // instead of showing a dash next to a state that asks
                        // for an action.
                        <button
                          type="button"
                          className="t-btn t-btn--primary t-btn--sm"
                          onClick={() => openDiligencia(d.taskId)}
                        >
                          Reintentar
                        </button>
                      ) : (
                        <span className="note">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager
            page={procPage}
            pageCount={Math.max(1, Math.ceil(procFiltered.length / pageSize))}
            total={procFiltered.length}
            pageSize={pageSize}
            onPage={setProcPage}
            onPageSize={(n) => {
              setPageSize(n);
              setProcPage(1);
            }}
            sizes={[8, 16, 24, 48]}
            unit="diligencias"
          />
        </div>
      )}

      {showTable && tab === 'hist' && (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <SortHead label="Diligencia" k="dil" w="22%" {...sortProps} />
                <SortHead
                  label="Receptor"
                  k="receptor"
                  w="18%"
                  {...sortProps}
                />
                <SortHead
                  label="Honorarios"
                  k="hon"
                  w="11%"
                  num
                  {...sortProps}
                />
                <SortHead label="Total" k="total" w="11%" num {...sortProps} />
                <SortHead label="Pagada" k="pagada" w="9%" num {...sortProps} />
                <Th w="13%" num>
                  Comprobante
                </Th>
                <Th w="16%" num>
                  Factura SII
                </Th>
              </tr>
            </thead>
            <tbody>
              {histSlice.map((d) => {
                // total = settledAmount; a row without it renders '—' rather
                // than a client-computed guess.
                return (
                  <tr
                    key={d.id}
                    onClick={() => d.taskId && openDiligencia(d.taskId)}
                  >
                    <td>
                      <div className="tbl__id">{d.rol}</div>
                      <div
                        className="tbl__primary"
                        style={{ fontWeight: 400, marginTop: 3 }}
                      >
                        {truncate(d.title, CARATULA_MAX)}
                      </div>
                    </td>
                    <td>{d.receptor}</td>
                    <td className="num">{fmtCLP(d.honorarios)}</td>
                    <td className="num tbl__primary">{fmtCLP(d.total)}</td>
                    <td className="num note">
                      {formatChileanDateShort(d.paidAt) ?? '—'}
                    </td>
                    <td
                      className="pg-doc-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {d.hasReceipt ? (
                        <button
                          type="button"
                          className="card__link"
                          onClick={() => handleComprobante(d)}
                        >
                          {ICON_DL}
                          Boleta
                        </button>
                      ) : (
                        <span
                          className="card__link card__link--disabled"
                          title="Comprobante no disponible"
                          aria-disabled="true"
                        >
                          {ICON_DL}
                          Boleta
                        </span>
                      )}
                    </td>
                    <td
                      className="pg-doc-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="pg-docs">
                        {d.taxDocument ? (
                          <TaxDocumentActions
                            doc={d.taxDocument}
                            onView={(doc) => handleViewFactura(doc, d.rol)}
                            onDownload={(doc) =>
                              handleDownloadFactura(doc, d.rol)
                            }
                          />
                        ) : d.creditNotes.length === 0 ? (
                          <span aria-label="Sin factura">—</span>
                        ) : null}
                        {d.creditNotes.map((nc) => (
                          <TaxDocumentActions
                            key={nc.id}
                            doc={nc}
                            kind="nc"
                            onView={(doc) =>
                              handleViewFactura(doc, d.rol, 'nc')
                            }
                            onDownload={(doc) =>
                              handleDownloadFactura(doc, d.rol, 'nc')
                            }
                          />
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager
            page={histPage}
            pageCount={Math.max(1, Math.ceil(histFiltered.length / pageSize))}
            total={histFiltered.length}
            pageSize={pageSize}
            onPage={setHistPage}
            onPageSize={(n) => {
              setPageSize(n);
              setHistPage(1);
            }}
            sizes={[8, 16, 24, 48]}
            unit="comprobantes"
          />
        </div>
      )}

      {/* Exp bulk pay only — the shared CheckoutModal (pricing + billing +
          Toku). The competing offers mount on top (children) for the
          seal-check selection. On close, if a pay queue remains, advance to
          the next selected diligencia. Single pay never opens this: cola rows
          navigate to the detail. */}
      {exp && (
        <CheckoutModal
          open={Boolean(openGroup)}
          onClose={() => {
            // On close, advance the pay queue to the next selected diligencia,
            // if any (the payments query is the source of truth for what got
            // paid).
            const next = payQueue[0];
            if (next) {
              openCheckout(next, payQueue.slice(1));
            } else {
              closeCheckout();
            }
          }}
          code={openGroup?.dilId}
          title={openGroup?.caseTitle || 'Diligencia'}
          quote={
            pickedOffer
              ? {
                  id: pickedOffer.id,
                  name: pickedOffer.name,
                  price: pickedOffer.price,
                  expectedPrice: pickedOffer.rawPrice,
                  fecha: formatChileanDateShort(pickedOffer.dateOffered),
                }
              : null
          }
          siblings={Math.max(0, (openGroup?.offers.length ?? 1) - 1)}
        >
          {openGroup && openGroup.offers.length > 1 && (
            <div className="pg-ofertas">
              <div className="pg-ofertas__head">
                <span className="pg-ofertas__label">
                  Ofertas en competencia
                </span>
                <span className="pg-ofertas__count">
                  {openGroup.offers.length}
                </span>
              </div>
              {openGroup.offers.map((o) => (
                <button
                  type="button"
                  key={o.id}
                  className={`pg-of${o.id === pickedOffer?.id ? ' is-on' : ''}`}
                  onClick={() => setPickedOfferId(o.id)}
                >
                  <span className="pg-of__seal" aria-hidden="true">
                    <SealCheckIcon />
                  </span>
                  <span className="pg-of__txt">
                    <span className="pg-of__n">{o.name}</span>
                    <span className="pg-of__s">
                      {o.dateOffered
                        ? `actúa el ${formatChileanDateShort(o.dateOffered)}`
                        : ''}
                    </span>
                  </span>
                  <span className="pg-of__amt">{fmtCLP(o.price)}</span>
                </button>
              ))}
            </div>
          )}
        </CheckoutModal>
      )}
    </div>
  );
}
